import { StateField, StateEffect } from '@codemirror/state';
import { Decoration, EditorView, keymap } from '@codemirror/view';

// State effects for format changes
export const toggleSubscriptEffect = StateEffect.define();
export const toggleSuperscriptEffect = StateEffect.define();
export const clearFormattingEffect = StateEffect.define();
export const setFormattingModeEffect = StateEffect.define();

// Helper to create decoration mark
const subscriptMark = Decoration.mark({ class: 'cm-subscript' });
const superscriptMark = Decoration.mark({ class: 'cm-superscript' });

// State field to track current formatting mode and range
export const formattingModeField = StateField.define({
  create() {
    return { mode: null, startPos: null }; // mode: null/'subscript'/'superscript', startPos: where continuous formatting started
  },
  
  update(value, tr) {
    // Check for explicit mode changes first
    for (const effect of tr.effects) {
      if (effect.is(setFormattingModeEffect)) {
        const newMode = effect.value;
        if (newMode === null) {
          return { mode: null, startPos: null };
        } else {
          // Start tracking from current position
          return { mode: newMode, startPos: tr.state.selection.main.head };
        }
      }
    }
    
    // Clear mode if user made a selection
    if (tr.selection && !tr.selection.main.empty) {
      return { mode: null, startPos: null };
    }
    
    // Clear mode if cursor moved to a different position (not just forward by typed characters)
    if (tr.selection && value.mode !== null) {
      const oldPos = tr.startState.selection.main.head;
      const newPos = tr.state.selection.main.head;
      const textAdded = tr.docChanged ? tr.state.doc.length - tr.startState.doc.length : 0;
      
      // If cursor moved somewhere other than forward by the amount of text added
      if (newPos !== oldPos + textAdded) {
        return { mode: null, startPos: null };
      }
    }
    
    return value;
  }
});

// State field to track formatted ranges
export const formattingField = StateField.define({
  create() {
    return {
      subscript: Decoration.none,
      superscript: Decoration.none
    };
  },

  update(value, tr) {
    let { subscript, superscript } = value;

    // Map existing decorations through document changes
    subscript = subscript.map(tr.changes);
    superscript = superscript.map(tr.changes);

    // Process formatting effects
    for (const effect of tr.effects) {
      if (effect.is(toggleSubscriptEffect)) {
        const { from, to } = effect.value;
        
        // Remove any overlapping superscript
        superscript = superscript.update({
          filter: (f, t) => t <= from || f >= to
        });
        
        // For continuous typing mode, we always want to add/extend formatting
        // Don't toggle off if we're in typing mode
        const modeInfo = tr.state.field(formattingModeField);
        const isTypingMode = modeInfo && modeInfo.mode === 'subscript';
        
        // Toggle subscript
        const hasSubscript = hasFormatInRange(subscript, from, to);
        if (hasSubscript && !isTypingMode) {
          subscript = subscript.update({
            filter: (f, t) => t <= from || f >= to
          });
        } else {
          // Add the new decoration and merge with adjacent ones
          const newDecorations = [];
          let mergedFrom = from;
          let mergedTo = to;
          
          // Collect all existing decorations first
          const existingDecorations = [];
          subscript.between(0, 1e8, (f, t, decoration) => {
            existingDecorations.push({ from: f, to: t, decoration });
          });
          
          // Check each existing decoration
          existingDecorations.forEach(({ from: f, to: t, decoration }) => {
            // Check if decorations are adjacent (with 1 position gap tolerance for insertion)
            const isAdjacent = (t === from) || (f === to) || (t === from - 1) || (f === to + 1);
            const isOverlapping = (t > from && f < to) || (t > mergedFrom && f < mergedTo);
            
            if (isAdjacent || isOverlapping) {
              mergedFrom = Math.min(f, mergedFrom);
              mergedTo = Math.max(t, mergedTo);
            } else {
              // Keep decorations that don't overlap with our merged range
              newDecorations.push(decoration.range(f, t));
            }
          });
          
          // Add the merged decoration
          newDecorations.push(subscriptMark.range(mergedFrom, mergedTo));
          
          // Sort decorations by position
          newDecorations.sort((a, b) => a.from - b.from);
          
          subscript = Decoration.set(newDecorations);
        }
      } 
      else if (effect.is(toggleSuperscriptEffect)) {
        const { from, to } = effect.value;
        
        // Remove any overlapping subscript
        subscript = subscript.update({
          filter: (f, t) => t <= from || f >= to
        });
        
        // For continuous typing mode, we always want to add/extend formatting
        // Don't toggle off if we're in typing mode
        const modeInfo = tr.state.field(formattingModeField);
        const isTypingMode = modeInfo && modeInfo.mode === 'superscript';
        
        // Toggle superscript
        const hasSuperscript = hasFormatInRange(superscript, from, to);
        if (hasSuperscript && !isTypingMode) {
          superscript = superscript.update({
            filter: (f, t) => t <= from || f >= to
          });
        } else {
          // Add the new decoration and merge with adjacent ones
          const newDecorations = [];
          let mergedFrom = from;
          let mergedTo = to;
          
          // Collect all existing decorations first
          const existingDecorations = [];
          superscript.between(0, 1e8, (f, t, decoration) => {
            existingDecorations.push({ from: f, to: t, decoration });
          });
          
          // Check each existing decoration
          existingDecorations.forEach(({ from: f, to: t, decoration }) => {
            // Check if decorations are adjacent (with 1 position gap tolerance for insertion)
            const isAdjacent = (t === from) || (f === to) || (t === from - 1) || (f === to + 1);
            const isOverlapping = (t > from && f < to) || (t > mergedFrom && f < mergedTo);
            
            if (isAdjacent || isOverlapping) {
              mergedFrom = Math.min(f, mergedFrom);
              mergedTo = Math.max(t, mergedTo);
            } else {
              // Keep decorations that don't overlap with our merged range
              newDecorations.push(decoration.range(f, t));
            }
          });
          
          // Add the merged decoration
          newDecorations.push(superscriptMark.range(mergedFrom, mergedTo));
          
          // Sort decorations by position
          newDecorations.sort((a, b) => a.from - b.from);
          
          superscript = Decoration.set(newDecorations);
        }
      }
      else if (effect.is(clearFormattingEffect)) {
        const { from, to } = effect.value;
        
        // Remove all formatting in range
        subscript = subscript.update({
          filter: (f, t) => t <= from || f >= to
        });
        superscript = superscript.update({
          filter: (f, t) => t <= from || f >= to
        });
      }
    }

    return { subscript, superscript };
  },

  provide: field => EditorView.decorations.from(field, value => {
    // Merge all decorations into a single set
    const decorations = [];
    
    // Add subscript decorations
    value.subscript.between(0, 1e8, (from, to, decoration) => {
      decorations.push(decoration.range(from, to));
    });
    
    // Add superscript decorations
    value.superscript.between(0, 1e8, (from, to, decoration) => {
      decorations.push(decoration.range(from, to));
    });
    
    // Sort by position and create decoration set
    decorations.sort((a, b) => a.from - b.from);
    
    
    return Decoration.set(decorations);
  })
});

// Helper to check if format exists in range
function hasFormatInRange(decorations, from, to) {
  let hasFormat = false;
  decorations.between(from, to, () => {
    hasFormat = true;
  });
  return hasFormat;
}

// Commands for keyboard shortcuts
export const toggleSubscript = (view) => {
  const selection = view.state.selection.main;
  
  if (selection.empty) {
    // Toggle mode for future typing
    const currentModeInfo = view.state.field(formattingModeField);
    const newMode = currentModeInfo.mode === 'subscript' ? null : 'subscript';
    view.dispatch({
      effects: setFormattingModeEffect.of(newMode)
    });
  } else {
    // Apply to selected text
    view.dispatch({
      effects: toggleSubscriptEffect.of({
        from: selection.from,
        to: selection.to
      })
    });
  }
  return true;
};

export const toggleSuperscript = (view) => {
  const selection = view.state.selection.main;
  
  if (selection.empty) {
    // Toggle mode for future typing
    const currentModeInfo = view.state.field(formattingModeField);
    const newMode = currentModeInfo.mode === 'superscript' ? null : 'superscript';
    view.dispatch({
      effects: setFormattingModeEffect.of(newMode)
    });
  } else {
    // Apply to selected text
    view.dispatch({
      effects: toggleSuperscriptEffect.of({
        from: selection.from,
        to: selection.to
      })
    });
  }
  return true;
};

export const clearFormatting = (view) => {
  const selection = view.state.selection.main;
  if (selection.empty) return false;
  
  view.dispatch({
    effects: clearFormattingEffect.of({
      from: selection.from,
      to: selection.to
    })
  });
  return true;
};

// Check if formatting is active at cursor
export const isFormatActive = (view, format) => {
  const selection = view.state.selection.main;
  
  // If no selection, check the formatting mode
  if (selection.empty) {
    const modeInfo = view.state.field(formattingModeField);
    return modeInfo.mode === format;
  }
  
  // If there's a selection, check if the range has formatting
  const { from, to } = selection;
  const field = view.state.field(formattingField);
  
  if (format === 'subscript') {
    return hasFormatInRange(field.subscript, from, to);
  } else if (format === 'superscript') {
    return hasFormatInRange(field.superscript, from, to);
  }
  
  return false;
};

// Keyboard shortcuts matching TipTap
export const formattingKeymap = keymap.of([
  { key: 'Ctrl-ArrowDown', run: toggleSubscript },
  { key: 'Cmd-ArrowDown', mac: 'Cmd-ArrowDown', run: toggleSubscript },
  { key: 'Ctrl-ArrowUp', run: toggleSuperscript },
  { key: 'Cmd-ArrowUp', mac: 'Cmd-ArrowUp', run: toggleSuperscript }
]);

// CSS styling for formatted text
export const formattingTheme = EditorView.baseTheme({
  '.cm-subscript': {
    verticalAlign: 'sub',
    fontSize: '0.8em',
    lineHeight: '1'
  },
  '.cm-superscript': {
    verticalAlign: 'super',
    fontSize: '0.8em',
    lineHeight: '1'
  }
});

// Input handler to apply formatting to typed text
const formattingInputHandler = EditorView.inputHandler.of((view, from, to, text) => {
  const modeInfo = view.state.field(formattingModeField);
  
  if (!modeInfo.mode || !text) return false;
  
  // Calculate the new cursor position
  const newPos = from + text.length;
  
  // For continuous typing, we just format the new character
  // The merging logic in the effect handler will take care of combining adjacent decorations
  const effect = modeInfo.mode === 'subscript' 
    ? toggleSubscriptEffect.of({ from, to: newPos })
    : toggleSuperscriptEffect.of({ from, to: newPos });
  
  // Dispatch both the text insertion and formatting in a single transaction
  view.dispatch({
    changes: { from, to, insert: text },
    effects: effect,
    selection: { anchor: newPos }
  });
  
  return true;
});

// Export complete extension
export const textFormattingExtension = [
  formattingField,
  formattingModeField,
  formattingKeymap,
  formattingTheme,
  formattingInputHandler
];